import React from "react";

import {DishDetailsItem} from "@apps/dish/interfaces";
import cx from "classnames";

import {EventOrigin} from "@globals/config/eventUtils";
import getDefaultDishDetailsImageSizeGroup from "@globals/functions/getDefaultDishDetailsImageSizeGroup";
import {IMAGE_SIZES} from "@globals/functions/getResolutionImgUrl";
import {ImageSizeGroup} from "@globals/functions/imageUtils";
import formatCurrency from "@globals/utils/formatCurrency";

import Button, {ButtonAccent} from "@components/Button";
import {useDishDetailsConfig} from "@components/DishDetails";
import {DishDetailsModalTrigger} from "@components/DishDetailsModal";
import FavoriteIcon from "@components/FavoriteIcon";
import ItemPurchaseButton from "@components/ItemPurchaseButton";
import MediaAlbumCarousel from "@components/MediaAlbumCarousel";
import MenuItemRibbon from "@components/MenuItemRibbon/MenuItemRibbon";
import Rating from "@components/Rating";
import RestaurantName from "@components/RestaurantName";
import {InlineTagList} from "@components/Tags";

import styles from "./MenuItem.scss";

type MenuItemProps = {
  item: DishDetailsItem;
  imageSize?: string;
  sizesAttribute?: string;
  imageSizeGroup?: ImageSizeGroup;
  wrapperClassName?: string;
  socialProofClassName?: string;
  menuItemList?: DishDetailsItem[];
  onQuickViewOpen?: () => void;
};

const defaultDishDetailsImageSizeGroup = getDefaultDishDetailsImageSizeGroup();

export default function MenuItem({
  item,
  imageSize = IMAGE_SIZES.DAILY_MENU,
  sizesAttribute,
  imageSizeGroup = defaultDishDetailsImageSizeGroup,
  wrapperClassName,
  socialProofClassName,
  menuItemList,
  onQuickViewOpen,
}: MenuItemProps): JSX.Element {
  const config = useDishDetailsConfig();
  const showPrice =
    config.isPriceShown(item) &&
    !config.hidePurchaseButton &&
    !config.showPriceInPurchaseButton;

  return (
    <div className={cx(styles.container, wrapperClassName)} role="listitem">
      <DishDetailsModalTrigger
        dish={item}
        title={item.item}
        menuItemList={menuItemList}
        onClose={config.preSelectedItem.closeCallback}
        isInitiallyOpen={config.preSelectedItem.slug === item.slug}
        onOpen={onQuickViewOpen}
      >
        <div>
          <div
            className={cx(styles.media, item.soldout && styles.soldout)}
            aria-label={`${item.item}, quick view`}
          >
            <MediaAlbumCarousel
              key={item.id}
              itemId={item.id}
              eventOrigin={EventOrigin.PAGE}
              keyPhoto={item.photo}
              mediaAlbum={item.media_album}
              imageSizeGroup={imageSizeGroup}
              size={imageSize}
              sizesAttribute={sizesAttribute}
              showThumbnails={false}
              wrapperClassName={styles.carousel}
            />
            <MenuItemRibbon
              itemId={item.item_id}
              almostGone={item.almost_gone}
              className={cx(
                item.soldout && styles.soldout,
                socialProofClassName,
                styles.socialProofContainer
              )}
            />
            <Button
              className={styles.quickViewButton}
              accent={ButtonAccent.AccentOrange}
              withBorder={true}
              aria-label={`${item.item}, quick view`}
            >
              Quick View
            </Button>
          </div>
          <div className={styles.titleContainer}>
            <h3 className={styles.title}>{item.item}</h3>
            {config.showFavorites && (
              <FavoriteIcon
                className={styles.favoriteIcon}
                itemId={item.item_id}
                showHoverBox={config.showFavoriteHoverBox}
              ></FavoriteIcon>
            )}
          </div>
        </div>
      </DishDetailsModalTrigger>
      <div className={styles.content}>
        <RestaurantName
          wrapperClassName={styles.restaurantLinkWrapper}
          restaurant={item.restaurant}
          operatorSlug={item.operator_slug}
          deliveryDate={config.selectedDate}
        />

        <div className={styles.detailsRow}>
          {!config.hideRating && item.average_rating && (
            <DishDetailsModalTrigger
              dish={item}
              title={item.item}
              openWithReviews={true}
              onOpen={onQuickViewOpen}
            >
              <Rating
                showOneStar
                averageRating={item.average_rating}
                reviewCount={item.review_count}
                className={styles.rating}
              />
            </DishDetailsModalTrigger>
          )}
          <InlineTagList tags={item.tags} />
          {config.hidePurchaseButton || (
            <ItemPurchaseButton
              wrapperClassName={styles.buyButton}
              item={item}
            />
          )}
          {showPrice && (
            <span className={styles.itemPrice}>
              {formatCurrency(Number(item.price))}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
